/**
 * Reports page — left sidebar with 6 reports, right content area.
 */

import { useState } from 'react';
import JobCompletionReport from '../components/reports/JobCompletionReport';
import VendorPerformanceReport from '../components/reports/VendorPerformanceReport';
import PartPricingHistoryReport from '../components/reports/PartPricingHistoryReport';
import PnlSummaryReport from '../components/reports/PnlSummaryReport';
import LaborUtilizationReport from '../components/reports/LaborUtilizationReport';
import OpenJobsAgingReport from '../components/reports/OpenJobsAgingReport';

const REPORTS = [
  { key: 'job-completion', label: 'Job Completion', icon: CheckIcon, Component: JobCompletionReport },
  { key: 'vendor-performance', label: 'Vendor Performance', icon: TruckIcon, Component: VendorPerformanceReport },
  { key: 'part-pricing', label: 'Part Pricing History', icon: TagIcon, Component: PartPricingHistoryReport },
  { key: 'pnl-summary', label: 'P&L Summary', icon: ChartIcon, Component: PnlSummaryReport },
  { key: 'labor-utilization', label: 'Labor Utilization', icon: ClockIcon, Component: LaborUtilizationReport },
  { key: 'open-jobs-aging', label: 'Open Jobs Aging', icon: CalendarIcon, Component: OpenJobsAgingReport },
];

export default function ReportsPage() {
  const [activeReport, setActiveReport] = useState('job-completion');
  const active = REPORTS.find((r) => r.key === activeReport) || REPORTS[0];
  const ActiveComponent = active.Component;

  return (
    <div className="flex flex-col lg:flex-row gap-4 h-full">
      {/* Sidebar — horizontal scroll on mobile, vertical sidebar on desktop */}
      <div className="lg:w-56 flex-shrink-0">
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="hidden lg:block px-3 py-2 bg-gray-50 border-b border-gray-200">
            <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Reports</h2>
          </div>
          <nav className="flex lg:flex-col py-1 overflow-x-auto lg:overflow-x-visible gap-1 lg:gap-0 px-1 lg:px-0">
            {REPORTS.map((r) => {
              const Icon = r.icon;
              return (
                <button
                  key={r.key}
                  onClick={() => setActiveReport(r.key)}
                  className={`flex items-center gap-2 px-3 py-2 text-sm text-left transition-colors whitespace-nowrap rounded-md lg:rounded-none ${
                    activeReport === r.key
                      ? 'bg-blue-50 text-blue-700 font-medium'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <Icon className="w-4 h-4 flex-shrink-0" />
                  {r.label}
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <h1 className="text-lg font-semibold text-foreground mb-3">{active.label}</h1>
        <ActiveComponent />
      </div>
    </div>
  );
}

/* ── Inline SVG icons ── */

function CheckIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

function TruckIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 18.75a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 01-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h1.125c.621 0 1.129-.504 1.09-1.124a17.902 17.902 0 00-3.213-9.193 2.056 2.056 0 00-1.58-.86H14.25M16.5 18.75h-2.25m0-11.177v-.958c0-.568-.422-1.048-.987-1.106a48.554 48.554 0 00-10.026 0 1.106 1.106 0 00-.987 1.106v7.635m12-6.677v6.677m0 4.5v-4.5m0 0h-12" />
    </svg>
  );
}

function TagIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9.568 3H5.25A2.25 2.25 0 003 5.25v4.318c0 .597.237 1.17.659 1.591l9.581 9.581c.699.699 1.78.872 2.607.33a18.095 18.095 0 005.223-5.223c.542-.827.369-1.908-.33-2.607L11.16 3.66A2.25 2.25 0 009.568 3z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 6h.008v.008H6V6z" />
    </svg>
  );
}

function ChartIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
    </svg>
  );
}

function ClockIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

function CalendarIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
    </svg>
  );
}
